/**
 * DJ-Grade Track Matcher Service
 *
 * Implements a two-mode matching system:
 * 1. "Official release" - ISRC-first with metadata corroboration
 * 2. "Remix-sensitive" - Penalizes version mismatches, blocks original↔remix confusion
 *
 * No universal cross-platform track ID exists, so we:
 * - Generate candidate sets via search
 * - Score candidates with a weighted model
 * - Keep top-N with full evidence
 * - Surface conflicts for user resolution
 */

import {
  AssetMetadata,
  AssetProvider,
  DEFAULT_MATCHER_CONFIG,
  MatchCandidate,
  MatcherConfig,
  MatchEvidence,
  MatchMode,
  MatchPenalty,
  MatchReason,
  MatchResult,
  PenaltyType,
  RecordingVersion,
  VersionType,
} from '../types/MatchTypes';
import {
  collapseInitialisms,
  foldDiacritics,
} from '../utils/trackTextNormalize';

export class TrackMatcherService {
  private config: MatcherConfig;

  constructor(config: Partial<MatcherConfig> = {}) {
    this.config = { ...DEFAULT_MATCHER_CONFIG, ...config };
  }

  // ===========================================================================
  // PUBLIC API
  // ===========================================================================

  /**
   * Match a source track against a list of candidates from a provider.
   * Returns the best match with full evidence, or null if no viable match.
   */
  public matchTrack(
    source: AssetMetadata,
    candidates: AssetMetadata[],
    provider: AssetProvider,
    mode: MatchMode = 'official_release',
  ): MatchResult | null {
    if (!candidates.length) {
      return null;
    }

    // Parse source version info
    const sourceVersion = this.parseVersion(source.title);

    // Score all candidates
    const scoredCandidates: MatchCandidate[] = candidates.map((candidate) => {
      const evidence = this.calculateEvidence(source, candidate, sourceVersion);
      const confidence = this.calculateConfidence(evidence, mode);
      const reasons = this.generateReasons(evidence, mode);

      return {
        providerId: (candidate as any).id || '',
        provider,
        metadata: candidate,
        confidence,
        isTopChoice: false,
        evidence,
        reasons,
      };
    });

    // Sort by confidence descending
    scoredCandidates.sort((a, b) => b.confidence - a.confidence);

    // Mark top choice
    if (scoredCandidates.length > 0) {
      scoredCandidates[0].isTopChoice = true;
    }

    // Get best candidate
    const best = scoredCandidates[0];

    // Determine if this is a match
    const isMatch = best.confidence >= this.config.thresholds.autoMatchMin;
    const requiresReview =
      !isMatch && best.confidence >= this.config.thresholds.reviewMin;

    return {
      candidateId: best.providerId,
      provider,
      confidence: best.confidence,
      mode,
      isMatch,
      requiresReview,
      evidence: best.evidence,
      reasons: best.reasons,
      alternativeCandidates: scoredCandidates.slice(
        1,
        this.config.maxCandidates,
      ),
      matchedAt: new Date(),
    };
  }

  /**
   * Quick check if a candidate is a "bad version" (karaoke, tribute, etc.)
   */
  public isBadVersion(title: string): boolean {
    const titleLower = title.toLowerCase();
    return this.config.badVersionMarkers.some((marker) =>
      titleLower.includes(marker),
    );
  }

  /**
   * Parse version information from a track title.
   */
  public parseVersion(title: string): RecordingVersion {
    // Extract parenthetical/bracketed version info
    // Collect ALL version markers, don't stop at first match
    const allVersionParts: string[] = [];

    // Check brackets first (most reliable for version info like [2025 Edit], [Remix])
    const bracketMatches = title.match(/\[([^\]]+)\]/g);
    if (bracketMatches) {
      bracketMatches.forEach((m) => {
        const content = m.replace(/[[\]]/g, '').trim();
        // Skip if it's just a featuring indicator
        if (!content.toLowerCase().startsWith('feat')) {
          allVersionParts.push(content);
        }
      });
    }

    // Check parentheses (but filter out featuring info)
    const parenMatches = title.match(/\(([^)]+)\)/g);
    if (parenMatches) {
      parenMatches.forEach((m) => {
        const content = m.replace(/[()]/g, '').trim();
        // Skip featuring info, only keep version info
        if (
          !content.toLowerCase().startsWith('feat') &&
          !content.toLowerCase().startsWith('ft.')
        ) {
          allVersionParts.push(content);
        }
      });
    }

    // Check dash suffix
    const dashMatch = title.match(/\s-\s([^([\]]+)$/);
    if (dashMatch) {
      allVersionParts.push(dashMatch[1].trim());
    }

    const versionText = allVersionParts.join(' ');

    const versionLower = versionText.toLowerCase();
    const version: RecordingVersion = {
      type: 'original',
      name: versionText || undefined,
    };

    // Detect version type
    if (
      versionLower.includes('remix') ||
      versionLower.includes('rmx') ||
      versionLower.includes('rework')
    ) {
      version.type = 'remix';
      // Try to extract remixer name
      const remixerMatch = versionText.match(/(.+?)\s*(?:remix|rmx|rework)/i);
      if (remixerMatch) {
        version.remixer = remixerMatch[1].trim();
      }
    } else if (versionLower.includes('vip') || versionLower.includes('v.i.p')) {
      version.type = 'vip';
    } else if (
      versionLower.includes('bootleg') ||
      versionLower.includes('unofficial')
    ) {
      version.type = 'bootleg';
    } else if (
      versionLower.includes('mashup') ||
      versionLower.includes('mash-up')
    ) {
      version.type = 'mashup';
    } else if (
      versionLower.includes('radio edit') ||
      versionLower.includes('radio version') ||
      versionLower.includes('single version')
    ) {
      version.type = 'radio_edit';
    } else if (
      versionLower.includes('edit') ||
      versionLower.includes('intro edit') ||
      versionLower.includes('quick hit')
    ) {
      version.type = 'edit';
    } else if (
      versionLower.includes('extended') ||
      versionLower.includes('club mix') ||
      versionLower.includes('club version')
    ) {
      version.type = 'extended';
    } else if (
      versionLower.includes('original mix') ||
      versionLower.includes('original version')
    ) {
      version.type = 'original';
    } else if (
      versionLower.includes('remaster') ||
      versionLower.match(/\d{4}\s*remaster/)
    ) {
      version.type = 'remaster';
      const yearMatch = versionText.match(/(\d{4})/);
      if (yearMatch) {
        version.year = parseInt(yearMatch[1]);
      }
    } else if (
      versionLower.includes('live') ||
      versionLower.includes('concert')
    ) {
      version.type = 'live';
      version.isLive = true;
    } else if (versionLower.includes('acoustic')) {
      version.type = 'acoustic';
      version.isAcoustic = true;
    } else if (
      versionLower.includes('instrumental') ||
      versionLower.includes('inst.')
    ) {
      version.type = 'instrumental';
      version.isInstrumental = true;
    } else if (
      versionLower.includes('acapella') ||
      versionLower.includes('a cappella')
    ) {
      version.type = 'acapella';
      version.isAccapella = true;
    } else if (versionLower.includes('cover')) {
      version.type = 'cover';
    }

    // Check for clean/explicit
    if (versionLower.includes('clean') || versionLower.includes('radio safe')) {
      version.isClean = true;
    }

    return version;
  }

  // ===========================================================================
  // EVIDENCE CALCULATION
  // ===========================================================================

  private calculateEvidence(
    source: AssetMetadata,
    candidate: AssetMetadata,
    sourceVersion: RecordingVersion,
  ): MatchEvidence {
    const candidateVersion = this.parseVersion(candidate.title);
    const penalties: MatchPenalty[] = [];

    // Check for bad version first
    if (this.isBadVersion(candidate.title)) {
      penalties.push({
        type: 'suspicious_title',
        value: 100, // Effectively blocks this candidate
        reason: `Detected karaoke/tribute/cover marker in: "${candidate.title}"`,
      });
    }

    // Title matching
    const titleResult = this.matchTitle(source.title, candidate.title);

    // Artist matching
    const artistResult = this.matchArtists(
      source.artist,
      source.artists,
      candidate.artist,
      candidate.artists,
    );

    // Duration matching
    const durationResult = this.matchDuration(
      source.duration,
      candidate.duration,
    );

    // ISRC matching
    const isrcMatch = !!(
      source.isrc &&
      candidate.isrc &&
      source.isrc.toUpperCase() === candidate.isrc.toUpperCase()
    );

    // Version matching
    const versionResult = this.matchVersion(
      sourceVersion,
      candidateVersion,
      penalties,
    );

    // Explicit flag matching
    const explicitMatch =
      source.isExplicit === undefined ||
      candidate.isExplicit === undefined ||
      source.isExplicit === candidate.isExplicit;

    if (
      source.isExplicit !== undefined &&
      candidate.isExplicit !== undefined &&
      source.isExplicit !== candidate.isExplicit
    ) {
      penalties.push({
        type: 'explicit_mismatch',
        value: 10,
        reason: `Explicit flag mismatch: source=${source.isExplicit}, candidate=${candidate.isExplicit}`,
      });
    }

    return {
      titleScore: titleResult.score,
      titleNormalized: titleResult.normalized,

      artistScore: artistResult.score,
      artistsMatched: artistResult.matched,
      artistsMissing: artistResult.missing,
      artistUnavailable: artistResult.unavailable,

      isrcMatch,
      isrcSource: source.isrc,
      isrcCandidate: candidate.isrc,

      durationScore: durationResult.score,
      durationDelta: durationResult.delta,
      durationSource: source.duration,
      durationCandidate: candidate.duration,
      durationUnavailable: durationResult.unavailable,

      versionScore: versionResult.score,
      versionSource: sourceVersion,
      versionCandidate: candidateVersion,
      versionConflict: versionResult.conflict,

      explicitMatch,

      penalties,
    };
  }

  // ===========================================================================
  // COMPONENT MATCHERS
  // ===========================================================================

  private matchTitle(
    sourceTitle: string,
    candidateTitle: string,
  ): {
    score: number;
    normalized: { source: string; candidate: string; similarity: number };
  } {
    const sourceNorm = this.normalizeTitle(sourceTitle);
    const candidateNorm = this.normalizeTitle(candidateTitle);

    // Calculate similarity
    const similarity = this.stringSimilarity(sourceNorm, candidateNorm);

    // Perfect match
    if (sourceNorm === candidateNorm) {
      return {
        score: 100,
        normalized: {
          source: sourceNorm,
          candidate: candidateNorm,
          similarity: 1,
        },
      };
    }

    // Word-set comparison: same words in different order
    // e.g., "Country Roads Take Me Home" vs "Take Me Home Country Roads"
    const sourceWords = new Set(
      sourceNorm.split(/\s+/).filter((w) => w.length > 2),
    );
    const candidateWords = new Set(
      candidateNorm.split(/\s+/).filter((w) => w.length > 2),
    );

    if (sourceWords.size > 0 && candidateWords.size > 0) {
      const intersection = [...sourceWords].filter((w) =>
        candidateWords.has(w),
      );
      const union = new Set([...sourceWords, ...candidateWords]);
      const wordOverlap = intersection.length / union.size;

      // If 80%+ of significant words match, treat as same title regardless of order
      if (wordOverlap >= 0.8 && intersection.length >= 2) {
        return {
          score: 95,
          normalized: {
            source: sourceNorm,
            candidate: candidateNorm,
            similarity: wordOverlap,
          },
        };
      }
    }

    // One contains the other (common for version differences)
    if (
      sourceNorm.includes(candidateNorm) ||
      candidateNorm.includes(sourceNorm)
    ) {
      return {
        score: 90,
        normalized: {
          source: sourceNorm,
          candidate: candidateNorm,
          similarity,
        },
      };
    }

    return {
      score: Math.round(similarity * 100),
      normalized: {
        source: sourceNorm,
        candidate: candidateNorm,
        similarity,
      },
    };
  }

  private matchArtists(
    sourceArtist: string,
    sourceArtists: string[] | undefined,
    candidateArtist: string,
    candidateArtists: string[] | undefined,
  ): {
    score: number;
    matched: string[];
    missing: string[];
    unavailable?: boolean;
  } {
    // Check if candidate artist is unknown/unavailable (common with some APIs)
    const candidateNorm = candidateArtist.toLowerCase().trim();
    if (
      !candidateArtist ||
      candidateNorm === 'unknown artist' ||
      candidateNorm === 'unknown' ||
      candidateNorm === 'various artists'
    ) {
      // Mark as unavailable so we can exclude from weighted average
      return { score: 0, matched: [], missing: [], unavailable: true };
    }

    // Normalize and split artists
    const sourceList = this.parseArtists(sourceArtist, sourceArtists);
    const candidateList = this.parseArtists(candidateArtist, candidateArtists);

    const matched: string[] = [];
    const missing: string[] = [];

    // Check each source artist against all candidate artists
    for (const sArtist of sourceList) {
      const sNorm = this.normalizeArtist(sArtist);
      const found = candidateList.some((cArtist) => {
        const cNorm = this.normalizeArtist(cArtist);
        return (
          sNorm === cNorm ||
          sNorm.includes(cNorm) ||
          cNorm.includes(sNorm) ||
          this.stringSimilarity(sNorm, cNorm) > 0.8
        );
      });

      if (found) {
        matched.push(sArtist);
      } else {
        missing.push(sArtist);
      }
    }

    // Also check if any candidate artist appears in source title (feat. patterns)
    // This helps with remixes where the original artist is in the title
    if (matched.length === 0) {
      const sourceTitleLower = sourceArtist.toLowerCase();
      for (const cArtist of candidateList) {
        const cNorm = this.normalizeArtist(cArtist);
        if (cNorm.length > 3 && sourceTitleLower.includes(cNorm)) {
          matched.push(cArtist);
        }
      }
    }

    // Primary artist match is most important
    const primaryMatched =
      sourceList.length > 0 &&
      candidateList.length > 0 &&
      (this.normalizeArtist(sourceList[0]) ===
        this.normalizeArtist(candidateList[0]) ||
        this.stringSimilarity(
          this.normalizeArtist(sourceList[0]),
          this.normalizeArtist(candidateList[0]),
        ) > 0.8);

    if (primaryMatched && matched.length > 0) {
      // Primary matched, bonus for additional matches
      const score = 70 + (matched.length / sourceList.length) * 30;
      return { score: Math.min(100, Math.round(score)), matched, missing };
    } else if (matched.length > 0) {
      // Some artists matched but not primary - still a decent match for remixes
      const matchRatio = matched.length / Math.max(sourceList.length, 1);
      const score = 50 + matchRatio * 40; // 50-90 range
      return { score: Math.round(score), matched, missing };
    }

    // No artist matches found - this is often due to API formatting differences
    // (e.g., "SNAP!" vs "Snap!", different featuring syntax, etc.)
    // Mark as unavailable so it doesn't tank the confidence score
    // when title/ISRC matching is strong
    return { score: 0, matched, missing, unavailable: true };
  }

  private matchDuration(
    sourceDuration: number | undefined,
    candidateDuration: number | undefined,
  ): { score: number; delta: number; unavailable?: boolean } {
    if (!sourceDuration || !candidateDuration) {
      // Can't compare - mark as unavailable so we can exclude from weighted avg
      return { score: 0, delta: 0, unavailable: true };
    }

    const delta = Math.abs(sourceDuration - candidateDuration);

    if (delta <= this.config.duration.exactToleranceMs) {
      return { score: 100, delta };
    }

    if (delta <= this.config.duration.closeToleranceMs) {
      // Linear decay from 100 to 80
      const factor =
        1 -
        (delta - this.config.duration.exactToleranceMs) /
          (this.config.duration.closeToleranceMs -
            this.config.duration.exactToleranceMs);
      return { score: Math.round(80 + factor * 20), delta };
    }

    if (delta <= this.config.duration.remixMaxDeltaMs) {
      // Linear decay from 80 to 40
      const factor =
        1 -
        (delta - this.config.duration.closeToleranceMs) /
          (this.config.duration.remixMaxDeltaMs -
            this.config.duration.closeToleranceMs);
      return { score: Math.round(40 + factor * 40), delta };
    }

    // Beyond max delta, very low score
    return { score: Math.max(0, 40 - Math.floor(delta / 10000) * 10), delta };
  }

  private matchVersion(
    source: RecordingVersion,
    candidate: RecordingVersion,
    penalties: MatchPenalty[],
  ): { score: number; conflict?: string } {
    // Same version type
    if (source.type === candidate.type) {
      // Check remixer if both are remixes
      if (source.type === 'remix' && source.remixer && candidate.remixer) {
        const remixerMatch =
          this.normalizeArtist(source.remixer) ===
          this.normalizeArtist(candidate.remixer);
        if (!remixerMatch) {
          penalties.push({
            type: 'remixer_mismatch',
            value: this.config.version.penaltyRemixerMismatch,
            reason: `Different remixer: "${source.remixer}" vs "${candidate.remixer}"`,
          });
          return {
            score: 50,
            conflict: `Different remixer: "${source.remixer}" vs "${candidate.remixer}"`,
          };
        }
      }
      return { score: 100 };
    }

    // Compatible versions (e.g., original and radio_edit are close)
    const compatiblePairs: [VersionType, VersionType][] = [
      ['original', 'radio_edit'],
      ['extended', 'original'],
      ['extended', 'radio_edit'],
      ['remaster', 'original'],
      ['remix', 'radio_edit'], // Remixes often have radio edits
      ['remix', 'extended'], // Remixes often have extended versions
    ];

    const isCompatible = compatiblePairs.some(
      ([a, b]) =>
        (source.type === a && candidate.type === b) ||
        (source.type === b && candidate.type === a),
    );

    if (isCompatible) {
      return { score: 70 };
    }

    // Incompatible versions - this is a critical mismatch
    const conflict = `Version mismatch: ${source.type} vs ${candidate.type}`;

    // Check if source has no explicit version info (defaulted to original)
    // In this case, be lenient - the source might actually be a remix but
    // the title doesn't include version info (e.g., "Danza Kuduro" by W&W, AXMO)
    const sourceHasNoVersionInfo = source.type === 'original' && !source.name;

    // Remix vs Original is normally a hard block, BUT:
    // If source has no version info and candidate is a remix, allow it with a warning
    // (The artists will be used to validate the match)
    if (
      (source.type === 'remix' && candidate.type === 'original') ||
      (source.type === 'original' && candidate.type === 'remix')
    ) {
      // If source had no version info, be lenient - trust artist matching instead
      if (sourceHasNoVersionInfo && candidate.type === 'remix') {
        penalties.push({
          type: 'version_mismatch',
          value: this.config.version.penaltyMismatch / 2, // Reduced penalty
          reason: `Source has no version info, candidate is ${candidate.type}`,
        });
        return { score: 50, conflict }; // Allow with moderate score instead of 0
      }

      penalties.push({
        type: 'version_mismatch',
        value: this.config.version.penaltyMismatch * 2,
        reason: `Critical: ${conflict}`,
      });
      return { score: 0, conflict };
    }

    penalties.push({
      type: 'version_mismatch',
      value: this.config.version.penaltyMismatch,
      reason: conflict,
    });

    return { score: 30, conflict };
  }

  // ===========================================================================
  // CONFIDENCE CALCULATION
  // ===========================================================================

  private calculateConfidence(
    evidence: MatchEvidence,
    mode: MatchMode,
  ): number {
    const w = this.config.weights;

    // Build weighted score, excluding unavailable components
    // This prevents penalizing matches when metadata isn't available
    let totalWeight = w.title + w.version;
    let weightedScore =
      evidence.titleScore * w.title + evidence.versionScore * w.version;

    // Only include artist in calculation if it was available
    if (!evidence.artistUnavailable) {
      totalWeight += w.artist;
      weightedScore += evidence.artistScore * w.artist;
    }

    // Only include duration in calculation if it was available
    if (!evidence.durationUnavailable) {
      totalWeight += w.duration;
      weightedScore += evidence.durationScore * w.duration;
    }

    let score = weightedScore / totalWeight;

    // ISRC match is the most reliable cross-platform identifier
    // Give a strong bonus when ISRCs match (it means it's the same recording)
    if (evidence.isrcMatch) {
      // ISRC match is very strong evidence - boost significantly
      score = Math.max(score, 80) + 15; // Minimum 95% with ISRC match
    }

    // When artist is unavailable but title matches very well, boost confidence
    // This helps match tracks where the destination API doesn't return artist data
    if (evidence.artistUnavailable && evidence.titleScore >= 80) {
      // Good title match without artist data - likely a correct match
      score = Math.max(score, 70);
    }

    // Fingerprint bonus (when available)
    if (evidence.fingerprintMatch === 'exact') {
      score += w.fingerprint;
    } else if (evidence.fingerprintMatch === 'near') {
      score += w.fingerprint * 0.7;
    }

    // Apply penalties
    const totalPenalty = evidence.penalties.reduce(
      (sum, p) => sum + p.value,
      0,
    );
    score -= totalPenalty;

    // Mode-specific adjustments
    if (mode === 'remix_sensitive') {
      // In remix mode, version mismatches are even more severe
      if (evidence.versionConflict) {
        score -= 20;
      }

      // Duration delta is more important in remix mode
      if (evidence.durationDelta > this.config.duration.remixMaxDeltaMs) {
        score -= 30;
      }
    }

    return Math.max(0, Math.min(100, Math.round(score)));
  }

  // ===========================================================================
  // REASON GENERATION
  // ===========================================================================

  private generateReasons(
    evidence: MatchEvidence,
    mode: MatchMode,
  ): MatchReason[] {
    const reasons: MatchReason[] = [];

    // ISRC match is a strong positive
    if (evidence.isrcMatch) {
      reasons.push({
        type: 'positive',
        code: 'ISRC_MATCH',
        message: `ISRC codes match: ${evidence.isrcSource}`,
      });
    }

    // Title match
    if (evidence.titleScore >= 90) {
      reasons.push({
        type: 'positive',
        code: 'TITLE_MATCH',
        message: 'Title matches closely',
      });
    } else if (evidence.titleScore < 60) {
      reasons.push({
        type: 'negative',
        code: 'TITLE_MISMATCH',
        message: `Title similarity low: "${evidence.titleNormalized.source}" vs "${evidence.titleNormalized.candidate}"`,
      });
    }

    // Artist match
    if (evidence.artistScore >= 90) {
      reasons.push({
        type: 'positive',
        code: 'ARTIST_MATCH',
        message: `Artists match: ${evidence.artistsMatched.join(', ')}`,
      });
    } else if (evidence.artistsMissing.length > 0) {
      reasons.push({
        type: 'warning',
        code: 'ARTIST_PARTIAL',
        message: `Missing artists: ${evidence.artistsMissing.join(', ')}`,
      });
    }

    // Duration
    if (evidence.durationScore >= 90) {
      reasons.push({
        type: 'positive',
        code: 'DURATION_MATCH',
        message: `Duration matches (Δ${Math.round(evidence.durationDelta / 1000)}s)`,
      });
    } else if (evidence.durationDelta > this.config.duration.closeToleranceMs) {
      reasons.push({
        type: 'warning',
        code: 'DURATION_MISMATCH',
        message: `Duration difference: ${Math.round(evidence.durationDelta / 1000)}s`,
      });
    }

    // Version conflict
    if (evidence.versionConflict) {
      reasons.push({
        type: 'negative',
        code: 'VERSION_CONFLICT',
        message: evidence.versionConflict,
      });
    }

    // Penalties
    for (const penalty of evidence.penalties) {
      reasons.push({
        type: 'negative',
        code: penalty.type.toUpperCase(),
        message: penalty.reason,
      });
    }

    return reasons;
  }

  // ===========================================================================
  // NORMALIZATION HELPERS
  // ===========================================================================

  /**
   * Normalize a track title for comparison.
   * Removes version info, special characters, normalizes case.
   */
  private normalizeTitle(title: string): string {
    let normalized = foldDiacritics(title)
      .toLowerCase()
      .replace(/\$/g, 's')
      // Remove parenthetical content (version info)
      .replace(/\s*\([^)]*\)/g, '')
      // Remove bracketed content
      .replace(/\s*\[[^\]]*\]/g, '')
      // Remove "- Version" suffix (including generic "edit", "mix", year patterns)
      .replace(
        /\s*-\s*(\d{4}\s*)?(radio edit|original mix|extended|remix|remaster|edit|mix|version|club mix).*/gi,
        '',
      )
      // Remove feat./ft./with
      .replace(/\s*(feat\.?|ft\.?|with)\s+.*/gi, '');

    normalized = collapseInitialisms(normalized);
    normalized = normalized.replace(/[^a-z0-9\s]/g, '');
    normalized = collapseInitialisms(normalized);
    return normalized.replace(/\s+/g, ' ').trim();
  }

  /**
   * Normalize an artist name for comparison.
   */
  private normalizeArtist(artist: string): string {
    let normalized = foldDiacritics(artist)
      .toLowerCase()
      .replace(/\$/g, 's')
      // Remove "The" prefix
      .replace(/^the\s+/i, '')
      // Normalize & variations before stripping punctuation
      .replace(/\s*&\s*/g, ' and ');

    normalized = collapseInitialisms(normalized);
    normalized = normalized.replace(/[^a-z0-9\s]/g, '');
    normalized = collapseInitialisms(normalized);
    return normalized.replace(/\s+/g, ' ').trim();
  }

  /**
   * Parse artist string(s) into a list.
   */
  private parseArtists(artist: string, artists?: string[]): string[] {
    if (artists && artists.length > 0) {
      return artists;
    }

    // Split by common delimiters
    return artist
      .split(/[,&]|(\s+(?:feat\.?|ft\.?|with|and|x|vs\.?)\s+)/i)
      .map((a) => a?.trim())
      .filter((a) => a && !a.match(/^(feat\.?|ft\.?|with|and|x|vs\.?)$/i));
  }

  /**
   * Calculate string similarity (0-1) using Levenshtein distance.
   */
  private stringSimilarity(a: string, b: string): number {
    if (a === b) return 1;
    if (!a.length || !b.length) return 0;

    const matrix: number[][] = [];

    for (let i = 0; i <= b.length; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= a.length; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= b.length; i++) {
      for (let j = 1; j <= a.length; j++) {
        if (b.charAt(i - 1) === a.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1,
          );
        }
      }
    }

    const distance = matrix[b.length][a.length];
    const maxLen = Math.max(a.length, b.length);
    return 1 - distance / maxLen;
  }
}

// Export singleton instance with default config
export const TrackMatcher = new TrackMatcherService();
